import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Document, Page, pdfjs } from 'react-pdf';
import Annotation from './Annotation';
import AnnotationInfoPanel from './AnnotationInfoPanel';
import TableAnnotationPanel from './TableAnnotationPanel';
import MediaAnnotationPanel from './MediaAnnotationPanel';
import './PDFViewer.scss';

// Set up PDF.js worker for react-pdf@5.7.2
// react-pdf@5.7.2 includes pdfjs v2.12.313
if (typeof window !== 'undefined') {
  // Use the worker that matches the bundled PDF.js version (2.12.313)
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.12.313/pdf.worker.min.js`;
}

/**
 * PDFViewer component for displaying PDFs inline with annotations
 * 
 * @param {Object} props
 * @param {string} props.pdfData - Base64 encoded PDF data
 * @param {string} props.fileName - Name of the PDF file
 * @param {Array} props.annotations - Array of annotation objects
 * @param {Function} props.onClose - Callback when viewer is closed
 * @param {Function} [props.onAnnotationClose] - Callback when annotation is closed
 * @param {boolean} [props.showToolbar] - Show toolbar with controls (default: true)
 */
const PDFViewer = ({
  pdfData,
  fileName,
  factuurvoorstelnummer,
  annotations = [],
  onClose,
  onAnnotationClose,
  showToolbar = true,
}) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(100);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [visibleAnnotations, setVisibleAnnotations] = useState(
    annotations.reduce((acc, ann) => ({ ...acc, [ann.id]: true }), {})
  );
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [wrapperWidth, setWrapperWidth] = useState(800);
  const wrapperRef = useRef(null);

  // Convert base64 to blob URL using useEffect
  useEffect(() => {
    if (!pdfData) {
      setError('No PDF data provided');
      setLoading(false);
      return;
    }

    try {
      const binaryString = atob(pdfData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      setError(null);

      // Cleanup the blob URL when component unmounts
      return () => {
        URL.revokeObjectURL(url);
      };
    } catch (err) {
      setError(`Failed to load PDF data: ${err.message}`);
      setLoading(false);
    }
   }, [pdfData]);

  // Measure wrapper width for PDF scaling
  useEffect(() => {
    const measureWrapper = () => {
      if (wrapperRef.current) {
        // Calculate 90% of the wrapper's parent container width
        const parentWidth = wrapperRef.current.parentElement?.clientWidth || window.innerWidth;
        setWrapperWidth(Math.max(parentWidth * 0.9, 400));
      }
    };

    // Initial measurement
    setTimeout(measureWrapper, 100);

    // Measure on window resize
    window.addEventListener('resize', measureWrapper);
    return () => window.removeEventListener('resize', measureWrapper);
  }, []);

  const onDocumentLoadSuccess = ({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    setError(`Failed to load PDF: ${error?.message || 'Unknown error'}`);
    setLoading(false);
  };

  const handlePreviousPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages || 1));
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 10, 50));
  };

  const handleAnnotationClose = (annotationId) => {
    // Annotations should persist and not be closeable
    // This is a no-op - we're keeping annotations visible
    if (onAnnotationClose) {
      onAnnotationClose(annotationId);
    }
  };

  const handleAnnotationSelect = (annotation) => {
    setSelectedAnnotation(annotation);
  };

  const handleAnnotationPanelClose = () => {
    setSelectedAnnotation(null);
  };

  // Get annotations for current page
  const currentPageAnnotations = annotations.filter(
    ann => (ann.page === undefined || ann.page === pageNumber) && 
            visibleAnnotations[ann.id] !== false
  );

  return (
    <div className="pdf-viewer">
      {/* Header with close button */}
      <div className="pdf-viewer__header">
        <h3 className="pdf-viewer__title">{fileName}</h3>
        <button
          className="pdf-viewer__close-btn"
          onClick={onClose}
          aria-label="Close PDF viewer"
        >
          ✕
        </button>
      </div>

      {/* Toolbar */}
      {showToolbar && (
        <div className="pdf-viewer__toolbar">
          <button
            className="pdf-viewer__btn"
            onClick={handlePreviousPage}
            disabled={pageNumber <= 1}
            title="Previous page"
          >
            ← Previous
          </button>

          <div className="pdf-viewer__page-info">
            <input
              type="number"
              min="1"
              max={numPages || 1}
              value={pageNumber}
              onChange={e => {
                const value = Math.min(Math.max(1, parseInt(e.target.value) || 1), numPages || 1);
                setPageNumber(value);
              }}
              className="pdf-viewer__page-input"
            />
            <span>/ {numPages || 0}</span>
          </div>

          <button
            className="pdf-viewer__btn"
            onClick={handleNextPage}
            disabled={pageNumber >= (numPages || 1)}
            title="Next page"
          >
            Next →
          </button>

          <div className="pdf-viewer__divider" />

          <button
            className="pdf-viewer__btn"
            onClick={handleZoomOut}
            disabled={scale <= 50}
            title="Zoom out"
          >
            −
          </button>

          <span className="pdf-viewer__scale">{scale}%</span>

          <button
            className="pdf-viewer__btn"
            onClick={handleZoomIn}
            disabled={scale >= 200}
            title="Zoom in"
          >
            +
          </button>
        </div>
      )}

      {/* PDF Container with Annotations Overlay */}
      <div className="pdf-viewer__container">
        {error && (
          <div className="pdf-viewer__error">
            <p>{error}</p>
          </div>
        )}

        {loading && (
          <div className="pdf-viewer__loading">
            <div className="pdf-viewer__spinner" />
            <p>Loading PDF...</p>
          </div>
        )}

        {pdfUrl && (
          <>
            <div className="pdf-viewer__pdf-wrapper" ref={wrapperRef} style={{ zoom: scale / 100 }}>
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<div>Loading...</div>}
              >
                <Page pageNumber={pageNumber} width={wrapperWidth} />
              </Document>

              {/* Annotations overlay */}
              <div className="pdf-viewer__annotations-layer">
                {currentPageAnnotations.map(annotation => (
                  <Annotation
                    key={annotation.id}
                    annotation={annotation}
                    onSelect={handleAnnotationSelect}
                  />
                ))}
              </div>
            </div>

            {/* Annotation Info Panel - rendered inside container but outside pdf-wrapper */}
            {selectedAnnotation && selectedAnnotation.annotationType === 'data' && (
              <AnnotationInfoPanel
                annotation={selectedAnnotation}
                onClose={handleAnnotationPanelClose}
              />
            )}
          </>
        )}
      </div>

      {/* Table Annotation Panel - rendered at root level to avoid DOM nesting issues */}
      {selectedAnnotation && selectedAnnotation.annotationType === 'table' && (
        <TableAnnotationPanel
          annotation={selectedAnnotation}
          onClose={handleAnnotationPanelClose}
        />
      )}

      {/* Media Annotation Panel - rendered at root level */}
      {selectedAnnotation && selectedAnnotation.annotationType === 'media' && (
        <MediaAnnotationPanel
          annotation={selectedAnnotation}
          onClose={handleAnnotationPanelClose}
        />
      )}
    </div>
  );
};

PDFViewer.propTypes = {
  pdfData: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  factuurvoorstelnummer: PropTypes.string,
  annotations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      x: PropTypes.number.isRequired,
      y: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
      title: PropTypes.string,
      type: PropTypes.oneOf(['info', 'warning', 'highlight']),
      page: PropTypes.number,
      visible: PropTypes.bool,
    })
  ),
  onClose: PropTypes.func.isRequired,
  onAnnotationClose: PropTypes.func,
  showToolbar: PropTypes.bool,
};

export default PDFViewer;
